import { defineStore } from 'pinia'
import { getContentReviewProfiles } from '@/content-review/api'
import { getCustomizableGoalProfiles } from '@/goals/api'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { CUSTOMIZATIONS, type CustomizationKey } from './customization_demo_catalog'
import {
  readStoredSelections,
  writeStoredSelections,
  type DemoSelections
} from './customization_demo_storage'

type AvailableProfileIds = Partial<Record<CustomizationKey, string[]>>

// Pages read the selections live: the content review store's `reviewProfileId` for auto-review, and
// the anchor page, which reloads when the goal customization selection changes.
export const useCustomizationDemoStore = defineStore('customizationDemo', {
  state: () => ({
    selectionByCustomization: readStoredSelections() as DemoSelections,
    availableProfileIdsByCustomization: {} as AvailableProfileIds,
    fetching: false,
    error: null as unknown
  }),
  actions: {
    // A category whose profile carries no customization would demo nothing at all, so the picker
    // checks up front and disables those options. Each customization is checked against its own
    // backend: a profile with a review rubric but no goal prompt can be demoed under Auto-Review and
    // not under Goal Customizations.
    async fetchAvailability() {
      this.fetching = true
      this.error = null
      try {
        const [customizableGoalProfileIds, reviewProfiles] = await Promise.all([
          getCustomizableGoalProfiles(useVisibilityStore().betaVisibility),
          getContentReviewProfiles()
        ])
        const available: AvailableProfileIds = {
          [CUSTOMIZATIONS.GOALS]: customizableGoalProfileIds,
          [CUSTOMIZATIONS.AUTO_REVIEW]: reviewProfiles
            .map((profile) => profile.content_profile)
            .filter(Boolean)
        }
        this.availableProfileIdsByCustomization = available
        this.dropStaleSelections(available)
      } catch (error) {
        this.error = error
      } finally {
        this.fetching = false
      }
    },
    // A selection restored from storage can name a profile the backend no longer reports — a
    // rubric unreleased in Airtable, or a refreshed cache. Disabling the option is not enough: the
    // selection would still be sent as content_profile. Drop those, leaving customizations the
    // backend said nothing about untouched.
    dropStaleSelections(available: AvailableProfileIds) {
      const stale = (Object.keys(available) as CustomizationKey[]).filter((customization) => {
        const selected = this.selectionByCustomization[customization]
        return !!selected && !available[customization]?.includes(selected)
      })
      if (!stale.length) return

      const next: DemoSelections = { ...this.selectionByCustomization }
      stale.forEach((customization) => {
        next[customization] = null
      })
      this.selectionByCustomization = next
      writeStoredSelections(next)
    },
    selectProfile(customization: CustomizationKey, profileId: string) {
      this.selectionByCustomization = {
        ...this.selectionByCustomization,
        [customization]: profileId || null
      }
      writeStoredSelections(this.selectionByCustomization)
    },
    turnOff(customization: CustomizationKey) {
      this.selectProfile(customization, '')
    },
    turnOffAll() {
      this.selectionByCustomization = {}
      writeStoredSelections(this.selectionByCustomization)
    }
  },
  getters: {
    demoProfileFor(state) {
      return (customization: CustomizationKey): string =>
        state.selectionByCustomization[customization] || ''
    },
    isProfileAvailable(state) {
      return (customization: CustomizationKey, profileId: string): boolean => {
        const checked = state.availableProfileIdsByCustomization[customization]
        return checked ? checked.includes(profileId) : true
      }
    },
    activeDemoCount(state): number {
      return Object.values(state.selectionByCustomization).filter(Boolean).length
    },
    isDemoActive(): boolean {
      return this.activeDemoCount > 0
    }
  }
})
